import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "q-mx-md" }
const _hoisted_2 = { class: "q-mx-md" }

import { computed, watch } from "vue";
import { FilePanelProps } from "@/types";
import { getIcon, uploadPanelOptions } from "@/util";
import { parseApiStore, projectSaveStore } from "@/hooks";
import {
  ExpansionItem,
  FileInput,
  SwitchInput,
  TextButton,
  FlexBox,
  SelectInput,
  List,
  AttributeChip,
} from "@/components/common";
import { ProjectFilesInput } from "@/components/project/save";
import {
  GitHubProjectInput,
  JiraProjectInput,
} from "@/components/integrations";
import UploadPanelErrors from "./UploadPanelErrors.vue";
import UploadPanelName from "./UploadPanelName.vue";


/**
 * Provides inputs for uploading a file.
 */
const __default__ = {
  name: "UploadPanel",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    panel: {},
    index: {},
    hideUploadType: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const variantOptions = computed(() =>
  props.index === 0 ? uploadPanelOptions() : uploadPanelOptions().slice(0, 2)
);

const label = computed(
  () =>
    ({
      artifact: "Artifact Type",
      trace: "Trace Matrix",
      bulk: "Bulk Upload",
      github: "GitHub Import",
      jira: "Jira Import",
    })[props.panel.variant]
);
const newLabel = computed(() => `New ${label.value}`);

const valid = computed(() => !props.panel.errorMessage);

const headerClass = computed(() =>
  valid.value ? "text-positive" : "text-negative"
);

const iconId = computed(() =>
  valid.value ? getIcon("success") : getIcon("error")
);

const hasSingleFile = computed(
  () =>
    (props.panel.variant === "artifact" || props.panel.variant === "trace") &&
    !props.panel.isGenerated
);
const hasBulkFiles = computed(
  () => props.panel.variant === "bulk" && !props.panel.emptyFiles
);

/**
 * Deletes the current file panel.
 */
function handleDeletePanel() {
  projectSaveStore.removePanel(props.panel.variant, props.index);
}

// Clear the name when the variant changes.
watch(
  () => props.panel.variant,
  () => {
    props.panel.name = "";
  }
);

// For artifact upload, set the name to the artifact type when the type changes.
watch(
  () => props.panel.type,
  (type) => {
    if (props.panel.variant === "artifact") {
      props.panel.name = type;
    }
  }
);

// For trace upload, set the name to the artifact types when the type changes.
watch(
  () => [props.panel.type, props.panel.toType],
  ([fromType, toType]) => {
    if (props.panel.variant === "trace") {
      props.panel.name =
        !props.panel.type || !props.panel.toType
          ? ""
          : `${fromType} to ${toType}`;
    }
  }
);

// For file upload, parse uploaded files and store the parse state.
watch(
  () => props.panel.file,
  (file) => {
    if (file) {
      parseApiStore.handleParseProjectFile(
        props.panel,
        projectSaveStore.artifactMap
      );
    } else {
      props.panel.artifacts = [];
      props.panel.traces = [];
      props.panel.itemNames = [];
      props.panel.errorMessage = undefined;
    }
  }
);

// For file upload, close the panel after successful upload.
watch(
  () => props.panel.loading,
  () => {
    if (props.panel.loading || !valid.value) return;

    props.panel.open = false;
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ExpansionItem), {
    modelValue: props.panel.open,
    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((props.panel.open) = $event)),
    label: props.panel.name || newLabel.value,
    caption: label.value,
    icon: iconId.value,
    "header-class": headerClass.value,
    "data-cy": "panel-files"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _renderSlot(_ctx.$slots, "panel", {
          panel: props.panel
        }),
        (!props.hideUploadType)
          ? (_openBlock(), _createBlock(_unref(SelectInput), {
              key: 0,
              modelValue: props.panel.variant,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((props.panel.variant) = $event)),
              label: "Upload Type",
              options: variantOptions.value,
              hint: "Required",
              class: "full-width q-mb-sm",
              "option-to-value": "",
              "option-label": "name",
              "option-value": "id",
              "data-cy": "input-upload-type"
            }, null, 8, ["modelValue", "options"]))
          : _createCommentVNode("", true),
        _createVNode(UploadPanelName, _normalizeProps(_guardReactiveProps(props)), null, 16),
        (hasSingleFile.value)
          ? (_openBlock(), _createBlock(_unref(FileInput), {
              key: 1,
              modelValue: props.panel.file,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((props.panel.file) = $event)),
              multiple: false,
              "data-cy": "input-files-panel"
            }, null, 8, ["modelValue"]))
          : _createCommentVNode("", true),
        (hasBulkFiles.value)
          ? (_openBlock(), _createBlock(_unref(ProjectFilesInput), {
              key: 2,
              modelValue: props.panel.bulkFiles,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((props.panel.bulkFiles) = $event)),
              tim: props.panel.tim,
              "onUpdate:tim": _cache[3] || (_cache[3] = ($event: any) => ((props.panel.tim) = $event)),
              "data-cy": "input-files-bulk"
            }, null, 8, ["modelValue", "tim"]))
          : _createCommentVNode("", true),
        (props.panel.variant === 'github')
          ? (_openBlock(), _createBlock(_unref(GitHubProjectInput), { key: 3 }))
          : _createCommentVNode("", true),
        (props.panel.variant === 'jira')
          ? (_openBlock(), _createBlock(_unref(JiraProjectInput), { key: 4 }))
          : _createCommentVNode("", true),
        (props.panel.variant === 'bulk')
          ? (_openBlock(), _createBlock(_unref(SwitchInput), {
              key: 5,
              modelValue: props.panel.emptyFiles,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((props.panel.emptyFiles) = $event)),
              label: "Create an empty project",
              "data-cy": "toggle-create-empty-project"
            }, null, 8, ["modelValue"]))
          : _createCommentVNode("", true),
        (!props.hideUploadType)
          ? (_openBlock(), _createBlock(_unref(ExpansionItem), {
              key: 6,
              label: "Advanced Settings"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(FlexBox), { column: "" }, {
                  default: _withCtx(() => [
                    (hasBulkFiles.value)
                      ? (_openBlock(), _createBlock(_unref(SwitchInput), {
                          key: 0,
                          modelValue: props.panel.summarize,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((props.panel.summarize) = $event)),
                          label: "Generate artifact summaries",
                          "data-cy": "toggle-create-summarize"
                        }, null, 8, ["modelValue"]))
                      : _createCommentVNode("", true),
                    (props.panel.variant === 'trace')
                      ? (_openBlock(), _createBlock(_unref(SwitchInput), {
                          key: 1,
                          modelValue: props.panel.isGenerated,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((props.panel.isGenerated) = $event)),
                          label: "Generate Trace Links"
                        }, null, 8, ["modelValue"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _createVNode(UploadPanelErrors, _normalizeProps(_guardReactiveProps(props)), null, 16),
                (hasSingleFile.value && props.panel.itemNames.length > 0)
                  ? (_openBlock(), _createBlock(_unref(List), {
                      key: 0,
                      class: "q-mb-md"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(ExpansionItem), { label: "Parsed Entities" }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_2, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.panel.itemNames, (itemName) => {
                                return (_openBlock(), _createBlock(_unref(AttributeChip), {
                                  key: itemName,
                                  value: itemName,
                                  icon: 
                  props.panel.variant === 'artifact' ? 'artifact' : 'trace'
                ,
                                  "data-cy": "button-file-entities"
                                }, null, 8, ["value", "icon"]))
                              }), 128))
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(FlexBox), {
          justify: "end",
          t: "2"
        }, {
          default: _withCtx(() => [
            (!props.hideUploadType)
              ? (_openBlock(), _createBlock(_unref(TextButton), {
                  key: 0,
                  text: "",
                  icon: "delete",
                  label: "Delete",
                  "data-cy": "button-delete-panel",
                  onClick: handleDeletePanel
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ])
    ]),
    _: 3
  }, 8, ["modelValue", "label", "caption", "icon", "header-class"]))
}
}

})